import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import funnelApi from "../services/funnelApi";
import { Funnel } from "../services/funnelApi";
import config from "../config";

const processFunnelHtml = (html: string): string => {
    if (!html) return '';
    
    const normalizeHost = (url: string): string => {
        try {
            return new URL(url).hostname.replace(/^www\./, '').replace(/\/$/, '');
        } catch {
            return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
        }
    };
    
    const currentHost = normalizeHost(config.host_url);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    doc.querySelectorAll('a[href]').forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (!href) return;
        
        try {
            const url = new URL(href);
            const urlHost = url.hostname.replace(/^www\./, '').replace(/\/$/, '');
            
            if (urlHost === currentHost || href.startsWith('/')) {
                anchor.setAttribute('target', '_top');
            }
        } catch {
            if (href.startsWith('/')) {
                anchor.setAttribute('target', '_top');
            }
        }
    });
    
    return doc.documentElement.innerHTML;
};

function FunnelPage() {
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [funnel, setFunnel] = useState<Funnel | null>(null);
    const [loading, setLoading] = useState(true);
    const [iframeLoading, setIframeLoading] = useState(true);
    const hasFetched = useRef(false);

    const processedHtml = useMemo(() => {
        return processFunnelHtml(funnel?.generatedHtml || '');
    }, [funnel?.generatedHtml]);

    useEffect(() => {
        const fetchFunnel = async () => {
            if (hasFetched.current) return;
            hasFetched.current = true;

            setError(null);
            setLoading(true);

            const funnelId = searchParams.get("id");
            const pathSlug = window.location.pathname.split("/funnel/")[1];

            if (!funnelId && !pathSlug) {
                setError("No funnel specified");
                return;
            }

            try {
                let response;
                if (funnelId) {
                    response = await funnelApi.getFunnel(funnelId);
                } else if (pathSlug) {
                    response = await funnelApi.getFunnelBySlug(pathSlug);
                }

                if (response?.success && response.funnel) {
                    setFunnel(response.funnel);
                } else {
                    setError(response?.error || "Funnel not found");
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching funnel:", err);
                setError("Failed to load funnel");
            }
            setLoading(false);
        };

        fetchFunnel();
    }, [searchParams]);

    // Set document title from funnel data
    useEffect(() => {
        if (funnel?.title) {
            document.title = funnel.title;
        }
    }, [funnel]);

    if (error) {
        return (
            <div style={{ background: "#f9fafb", minHeight: "100vh", padding: "40px 20px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <div style={{ background: "white", padding: "40px", borderRadius: "16px", textAlign: "center", maxWidth: "400px" }}>
                    <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: "24px", color: "#dc2626" }}></i>
                    </div>
                    <h2 style={{ color: "#1a1a1a", marginBottom: "8px" }}>Error</h2>
                    <p style={{ color: "#374151", marginBottom: "24px" }}>{error}</p>
                    <a href="/" style={{ display: "inline-block", padding: "10px 24px", background: "#6366f1", color: "white", borderRadius: "8px", textDecoration: "none", fontWeight: "500" }}>Go Home</a>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: "100%", minHeight: "100vh", overflow: "auto", position: "relative" }}>
            {(loading || iframeLoading) && (
                <div style={{ position: "absolute", inset: 0, background: "#f9fafb", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "16px", zIndex: 100 }}>
                    <div style={{ width: "50px", height: "50px", border: "4px solid #e5e7eb", borderTop: "4px solid #6366f1", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                    <p style={{ color: "#6b7280", fontSize: "16px", margin: 0 }}>Loading...</p>
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
            )}
            <style>{`
                body { margin: 0; overflow-x: hidden; }
            `}</style>
            {!loading && (
                <iframe
                    srcDoc={processedHtml}
                    style={{
                        width: "100%",
                        minHeight: "100vh",
                        border: "none",
                        display: "block",
                        opacity: iframeLoading ? 0 : 1,
                    }}
                    title={funnel?.title || "Funnel"}
                    sandbox="allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation"
                    onLoad={() => setIframeLoading(false)}
                />
            )}
        </div>
    );
}

export default FunnelPage;
